import { useCallback, useEffect, useState } from 'react';
import { BeakerIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import type { AdminUser, AuditLogEntry, Locale } from '@xphoto/shared';
import { AUDIT_LOG_DEFAULT_LIMIT } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatDateTime } from '../viewer/format';
import { fetchUsers } from './admin-api';
import { fetchAuditActions, fetchAuditLog } from './audit-api';
import { DynamicFilterSelect } from './FilterSelect';

/**
 * Onglet « Audit » de la console SUPERADMIN (audit B1) — consultation du journal
 * `audit_log`.
 *
 * POURQUOI CET ÉCRAN. `audit_log` était écrite depuis 14 endroits (connexion,
 * changement de mot de passe, réinitialisation, et TOUS les jobs de traitement) et
 * n'avait AUCUN lecteur en production : personne ne pouvait répondre à « qui a supprimé
 * / déplacé / publié quoi ». Le même lot a propagé `user_id` jusqu'aux inserts des jobs,
 * sans quoi cet écran n'aurait affiché que des actions anonymes.
 *
 * DISTINCT DE « Journaux » (`request_logs`), volontairement : celui-ci est un journal
 * TECHNIQUE et volumineux (toutes les requêtes HTTP, 3 mois de rétention, purgeable) ;
 * celui-là trace les actions MÉTIER irréversibles (24 mois, NON purgeable depuis l'UI).
 *
 * ROBUSTESSE — `details` est un `jsonb` NON BORNÉ rempli librement par chaque site
 * d'écriture : aucun schéma commun, aucune forme garantie. Le rendu ne présume donc
 * RIEN (cf. `DetailsCell`) et ne doit JAMAIS faire planter la page.
 */

const PAGE_SIZE = AUDIT_LOG_DEFAULT_LIMIT;

export function AuditLogSection(): React.JSX.Element {
  const { t, locale } = useI18n();
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  // Filtres : Action (verbe métier) / Utilisateur (auteur) / bornes de date.
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchAuditLog(
      PAGE_SIZE,
      offset,
      {
        ...(action !== '' ? { action } : {}),
        ...(userId !== '' ? { userId } : {}),
        // Les champs `date` rendent `AAAA-MM-JJ` : on les convertit en instants ISO.
        // `from` = début du jour (inclusif), `to` = début du LENDEMAIN (la borne haute
        // est exclusive côté serveur) — sinon filtrer « jusqu'au 12 » exclurait le 12.
        ...(from !== '' ? { from: startOfDayIso(from) } : {}),
        ...(to !== '' ? { to: startOfNextDayIso(to) } : {}),
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return;
        setEntries(page.entries);
        setTotal(page.total);
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [offset, reloadKey, action, userId, from, to]);

  // Alimente les deux listes déroulantes (best-effort : leur échec ne bloque rien).
  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setUsers(list);
      })
      .catch(() => {
        /* non bloquant */
      });
    fetchAuditActions(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setActions(res.actions);
      })
      .catch(() => {
        /* non bloquant */
      });
    return () => controller.abort();
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const onFilterChange =
    (setter: (v: string) => void) =>
    (v: string): void => {
      setter(v);
      setOffset(0);
    };

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + entries.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const userOptions = users.map((u) => ({ value: u.id, label: `${u.displayName} — ${u.email}` }));
  const actionOptions = actions.map((a) => ({ value: a, label: a }));

  return (
    <section
      id="admin-audit"
      role="tabpanel"
      aria-labelledby="admin-audit-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-audit-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.audit.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.audit.intro')}</p>
        <p className="inline-flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
          <ShieldCheckIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t('admin.audit.retention')}
        </p>
      </div>

      {/* Barre de filtres : Action / Utilisateur / Du / Au. */}
      <div className="flex flex-wrap items-end gap-3">
        <DynamicFilterSelect
          id="audit-filter-action"
          labelKey="admin.audit.filterAction"
          allLabelKey="admin.audit.actionAll"
          value={action}
          options={actionOptions}
          onChange={onFilterChange(setAction)}
        />
        <DynamicFilterSelect
          id="audit-filter-user"
          labelKey="admin.audit.filterUser"
          allLabelKey="admin.audit.userAll"
          value={userId}
          options={userOptions}
          onChange={onFilterChange(setUserId)}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor="audit-filter-from" className="text-sm font-medium text-fg">
            {t('admin.audit.filterFrom')}
          </label>
          <input
            id="audit-filter-from"
            type="date"
            value={from}
            onChange={(e) => onFilterChange(setFrom)(e.target.value)}
            className="h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="audit-filter-to" className="text-sm font-medium text-fg">
            {t('admin.audit.filterTo')}
          </label>
          <input
            id="audit-filter-to"
            type="date"
            value={to}
            onChange={(e) => onFilterChange(setTo)(e.target.value)}
            className="h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          />
        </div>
      </div>

      {status === 'error' ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
          <p className="text-fg">{t('admin.errorTitle')}</p>
          <button
            type="button"
            onClick={reload}
            className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {t('admin.retry')}
          </button>
        </div>
      ) : status === 'ready' ? (
        <>
          <AuditTable entries={entries} locale={locale} />
          {total > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted" role="status">
                {interpolate(
                  t('admin.audit.range'),
                  { from: rangeFrom, to: rangeTo, total },
                  locale,
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.audit.previous')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.audit.next')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}
    </section>
  );
}

function AuditTable({
  entries,
  locale,
}: {
  entries: readonly AuditLogEntry[];
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  if (entries.length === 0) {
    return <p className="py-6 text-center text-muted">{t('admin.audit.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[48rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-start text-xs font-medium text-muted">
            <th scope="col" className="p-2 text-start">
              {t('admin.audit.colTimestamp')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.audit.colAction')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.audit.colUser')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.audit.colSummary')}
            </th>
            <th scope="col" className="p-2 text-start">
              {t('admin.audit.colDetails')}
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} locale={locale} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditRow({ entry, locale }: { entry: AuditLogEntry; locale: Locale }): React.JSX.Element {
  const { t } = useI18n();
  const when = formatDateTime(entry.createdAt, locale) ?? entry.createdAt;
  return (
    <tr className="border-b border-border">
      <td className="p-2 align-top whitespace-nowrap text-muted">{when}</td>
      <td className="p-2 align-top">
        <span className="font-medium text-fg">{entry.action}</span>
        {/* Une simulation n'a RIEN modifié : le dire explicitement (icône + texte,
            jamais la couleur seule) évite de lire une ligne d'aperçu comme un acte. */}
        {entry.dryRun && (
          <span className="mt-0.5 flex items-center gap-1 text-xs text-muted">
            <BeakerIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('admin.audit.dryRun')}
          </span>
        )}
      </td>
      <td className="p-2 align-top text-muted">
        <span className="break-all">{entry.userEmail ?? t('admin.audit.noUser')}</span>
      </td>
      <td className="p-2 align-top text-fg">{entry.summary ?? '—'}</td>
      <td className="p-2 align-top">
        <DetailsCell details={entry.details} />
      </td>
    </tr>
  );
}

/**
 * Rendu DÉFENSIF de `details` (`jsonb` non borné, rempli librement par chaque site
 * d'écriture). Aucune forme n'est présumée : on ne cherche AUCUN champ, on sérialise.
 *
 * `JSON.stringify` peut lever (référence circulaire) et rendrait alors la page blanche :
 * l'appel est donc enveloppé, et l'échec dégrade en tiret plutôt que de faire tomber
 * tout l'écran d'administration. La chaîne est bornée à `MAX` caractères — un `details`
 * de plusieurs mégaoctets (paramètres d'un job de masse) figerait le rendu.
 */
function DetailsCell({ details }: { details: unknown }): React.JSX.Element {
  const { t } = useI18n();
  if (details === null || details === undefined) {
    return <span className="text-muted">—</span>;
  }
  const MAX = 400;
  let text: string;
  try {
    text = typeof details === 'string' ? details : JSON.stringify(details);
  } catch {
    return <span className="text-muted">{t('admin.audit.detailsUnreadable')}</span>;
  }
  if (typeof text !== 'string' || text.length === 0) {
    return <span className="text-muted">—</span>;
  }
  const truncated = text.length > MAX;
  return (
    <details className="max-w-md">
      <summary className="cursor-pointer text-xs text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
        {t('admin.audit.showDetails')}
      </summary>
      <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-surface-2 p-2 text-xs whitespace-pre-wrap break-all text-fg">
        {truncated ? `${text.slice(0, MAX)}…` : text}
      </pre>
    </details>
  );
}

/**
 * `AAAA-MM-JJ` (valeur d'un `<input type="date">`) → instant ISO du DÉBUT de ce jour,
 * dans le fuseau du navigateur. `new Date('2026-07-18')` serait interprété en UTC ;
 * on construit donc la date par composants pour rester en heure locale, faute de quoi
 * un filtre « du 18 » raterait les premières heures du 18 pour un fuseau à l'est.
 */
function startOfDayIso(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map((n) => Number(n));
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0).toISOString();
}

/** Idem, mais le début du LENDEMAIN — la borne haute serveur étant EXCLUSIVE. */
function startOfNextDayIso(yyyyMmDd: string): string {
  const [y, m, d] = yyyyMmDd.split('-').map((n) => Number(n));
  return new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1, 0, 0, 0, 0).toISOString();
}
